import * as React from "react";
import { prisma } from "~/db.server";
import type { ActionFunctionArgs, HeadersFunction, LoaderFunctionArgs, MetaFunction } from "~/remix";
import { Link, json, redirect, useActionData, useLoaderData, useSearchParams } from "~/remix";

import { createUserSession, getSession, getUserId } from "~/session.server";

import invariant from "tiny-invariant";
import InputField from "~/components/Form/InputField";
import type { TwitchUserDetail } from "~/helpers/twitch.server";
import { getTwitchBasicAuthLink } from "~/helpers/twitch.server";
import _ from "~/helpers/utils";
import { createUser, getUserByEmail } from "~/models/user.server";
import { safeRedirect, validateEmail } from "~/utils";
import { InviteModeButton } from ".";
import BaseLayout from "./__layout";
import { CommonFormLayout, SubmitButton, TwitchLoginLinkButton } from "./login";

export const headers: HeadersFunction = () => {
  return {
    'Cache-Control': 'public, max-age=3600',
  };
};

export async function loader({ request }: LoaderFunctionArgs) {
  const userId = await getUserId(request);
  const sp = new URL(request.url).searchParams;
  const redirectTo = sp.get('redirectTo');
  if (userId) return redirect(redirectTo ?? '/');
  const session = await getSession(request);
  const twitchUser = session.getTwitchUser()
  const hasTwitchUser = twitchUser !== undefined;
  session.setRedirectTo(redirectTo);

  return json({
    twitchUser: twitchUser,
    twitchAuthLink: getTwitchBasicAuthLink(''),
    hasTwitchUser,
  }, await session.commitSessionHeader());
}

export async function action({ request }: ActionFunctionArgs) {
  const session = await getSession(request)
  const formData = await request.formData();
  const email = formData.get("email");
  const password = formData.get("password");
  const displayName = formData.get("displayName");
  const redirectTo = safeRedirect(formData.get("redirectTo"), "/");

  const isProd = !_.isDev();

  if (!validateEmail(email) && isProd) {
    return json(
      { errors: { email: "Email invalide", password: null } },
      { status: 400 }
    );
  }

  if (typeof password !== "string" || password.length === 0) {
    return json(
      { errors: { email: null, password: "Mor de passe requis" } },
      { status: 400 }
    );
  }

  if (password.length < 8 && isProd) {
    return json(
      { errors: { email: null, password: "Mot de passe trop court (min 8)" } },
      { status: 400 }
    );
  }
  // Always true
  invariant(typeof email === 'string');
  const existingUser = await getUserByEmail(email);
  if (existingUser) {
    return json(
      {
        errors: {
          email: "Il existe déjà un utilisateur avec cet identifiant, vous peut-être ?",
          password: null,
        },
      },
      { status: 400 }
    );
  }

  const user = await createUser(email, password, String(displayName));

  const twitchUser = session.getTwitchUser();

  if (twitchUser) {
    await prisma.oAuthToken.update({
      data: {
        userId: user.id,
      },
      where: {
        tokenKey: twitchUser.id,
      }
    })
  }

  return createUserSession({
    request,
    userId: user.id,
    remember: false,
    redirectTo,
  });
}

export const meta: MetaFunction = () => {
  return [{
    title: "Se créer un compte",
  }];
};

function TwitchBanner({ twitchUser }: { twitchUser: TwitchUserDetail }) {
  return <div>
    Pour vous authentifier via Twitch vous devez quand même vous créer un compte.<br />
    Certaines zones sont préremplies, elles sont issues de twitch mais ne nécessitent pas de rester identiques.<br />
    Si vous disposez déjà d'un compte chez nous, <Link
      className="text-blue-500 underline"
      to={{
        pathname: "/login",
      }}
    >identifiez-vous pour lier vos deux comptes.</Link>
  </div>
}

function Join() {
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirectTo") ?? undefined;
  const actionData = useActionData<typeof action>();
  const { twitchUser } = useLoaderData<typeof loader>();
  const emailRef = React.useRef<HTMLInputElement>(null);
  const passwordRef = React.useRef<HTMLInputElement>(null);
  const displayNameRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (actionData?.errors?.email) {
      emailRef.current?.focus();
    } else if (actionData?.errors?.password) {
      passwordRef.current?.focus();
    }
  }, [actionData]);

  return (<CommonFormLayout>
    {twitchUser && <TwitchBanner twitchUser={twitchUser} />}
    <InputField
      label="Votre email"
      name="email"
      ref={emailRef}
      required
      className="? !rounded-md"
      autoComplete="off"
      autoFocus={false}
      onBlur={(e) => {
        const { currentTarget: { value } } = e;
        if (displayNameRef.current && displayNameRef.current.value.trim().length === 0) {
          displayNameRef.current.value = value.split('@')[0]
        }
      }}
      defaultValue={twitchUser?.email}
    />
    {/* <div data-tip="Oui c'est en clair, qui en 2023 est encore dans un cybercafé, entrouré d'espions?"
      className="? tooltip w-full tooltip-bottom"> */}
    <InputField
      label="Mot de passe"
      type="password"
      name="password"
      ref={passwordRef}
      required
      autoComplete="off"
      autoFocus={false}
      className="? !rounded-md"
    />
    {/* </div> */}
    <InputField
      label="Pseudonyme"
      name="displayName"
      ref={displayNameRef}
      autoComplete="off"
      autoFocus={false}
      defaultValue={twitchUser?.display_name}
      className="? !rounded-md"
    />
    <input type="hidden" name="redirectTo" value={redirectTo} />
    <SubmitButton>
      Créer son compte
    </SubmitButton>

    <TwitchLoginLinkButton />

    <InviteModeButton />

    <div className="flex items-center justify-center">
      <div className="text-center text-sm text-gray-500">
        Vous avez déjà un compte {" "}
        <Link
          className="text-blue-500 underline"
          to={{
            pathname: "/login",
            search: searchParams.toString(),
          }}
        >
          Identifiez-vous
        </Link>
      </div>
    </div>
  </CommonFormLayout>
  );
}

export default function JoinIndex() {
  return <BaseLayout><Join /></BaseLayout>
}